import { dashboardRequest } from "../providers/aws/client";

export const FIRST_ECHO_PAYGATE_EXPERIMENT_ID =
  "first-echo-paygate-meta-ads-v1";
// The bot files every commerce fact of the live contact paywall under this
// assignment id (bot/commerce/paywall.py PAYWALL_ASSIGNMENT), whatever the
// contact's acquisition route.
export const CONTACT_PAYWALL_EXPERIMENT_ID = "contact-paywall-v1";

export type ExperimentMetrics = {
  variant: string;
  assigned_contacts: number;
  exposed_contacts: number;
  echo_started_contacts: number;
  echo_delivered_contacts: number;
  rated_contacts: number;
  five_star_contacts: number;
  offer_prompted_contacts: number;
  offer_selected_contacts: number;
  checkout_created_contacts: number;
  paywall_contacts: number;
  paywall_views: number;
  paid_contacts: number;
  paid_orders: number;
  revenue_minor: number;
  refunded_minor: number;
  net_revenue_minor: number;
  currency: string | null;
  credits_granted: number;
  internal_credits_granted: number;
  refund_events: number;
  partial_refund_events: number;
  dispute_events: number;
  dispute_won_events: number;
  eligibility_verified_contacts?: number;
  eligibility_mismatch_contacts?: number;
  assignment_to_delivery_pct: number | null;
  delivery_to_rating_pct: number | null;
  rating_to_prompt_pct: number | null;
  five_star_to_prompt_pct: number | null;
  prompt_to_selection_pct: number | null;
  selection_to_checkout_pct: number | null;
  checkout_to_paid_pct: number | null;
  prompt_to_paid_pct: number | null;
  assignment_to_paid_pct: number | null;
  paywall_to_paid_pct: number | null;
  paywall_to_referral_pct?: number | null;
  exposure_to_paid_pct: number | null;
  offers: OfferMetrics[];
  acquisition_routes?: AcquisitionRouteMetrics[];
  referral_qualified_contacts?: number;
  referral_rewarded_contacts?: number;
  referral_notified_contacts?: number;
  referral_rewards_granted?: number;
  referral_rewards_skipped?: number;
  referral_credits_granted?: number;
  // Abandoned checkouts and the reminder that tries to save them.
  cart_abandoned_contacts?: number;
  cart_recovery_prompted_contacts?: number;
  cart_recovery_prompts?: number;
  checkout_abandoned_pct?: number | null;
  cart_recovery_to_paid_pct?: number | null;
  paid_orders_after_recovery?: number;
  // Sales, repeat-purchase and retention facts (a contact can buy many times).
  repeat_paid_contacts?: number;
  repeat_purchase_pct?: number | null;
  orders_per_paid_contact?: number | null;
  average_order_value_minor?: number | null;
  net_revenue_per_paid_contact_minor?: number | null;
  net_revenue_per_assigned_contact_minor?: number | null;
  paywall_views_per_paywalled_contact?: number | null;
  median_minutes_to_first_payment?: number | null;
  post_purchase_echo_started_contacts?: number;
  post_purchase_activation_pct?: number | null;
  first_payment_at?: string | null;
  last_payment_at?: string | null;
  paid_orders_after_paywall?: number;
  paid_orders_after_offer?: number;
  paid_orders_unattributed?: number;
};

export type ExperimentDailyActivity = {
  date: string;
  assigned_contacts: number;
  echoes_started: number;
  paywall_views: number;
  paywall_contacts: number;
  offers_prompted: number;
  offer_contacts: number;
  checkouts_created: number;
  carts_abandoned: number;
  cart_recovery_prompts: number;
  paid_orders: number;
  paid_contacts: number;
  revenue_minor: number;
  referral_rewards_granted: number;
};

export type AcquisitionRouteMetrics = {
  acquisition_surface: string;
  assigned_contacts: number;
  paid_contacts: number;
  paid_orders: number;
  revenue_minor: number;
  assignment_to_paid_pct: number | null;
};

export type OfferMetrics = {
  offer_id: string;
  selected_contacts: number;
  checkout_contacts: number;
  paid_contacts: number;
  paid_orders: number;
  revenue_minor: number;
  currency: string | null;
  echoes_granted: number;
  internal_credits_granted: number;
  selection_to_paid_pct: number | null;
};

export type ExperimentSummary = {
  experiment_id: string;
  variants: ExperimentMetrics[];
  totals: ExperimentMetrics;
  daily?: ExperimentDailyActivity[];
  event_count: number;
  truncated: boolean;
};

export type ExperimentAssignment = {
  experiment_id: string;
  assignment_id: string;
  variant: string;
  user_id: string;
  assigned_at?: string;
  latest_event_at?: string;
  reason?: string;
  matched_rule_id?: string;
  bucket?: number;
  config_release_id?: string;
  config_digest?: string;
  eligibility_match?: boolean;
  eligibility_reason?: string;
  acquisition_source?: string;
  acquisition_group?: string;
  acquisition_platform?: string;
  acquisition_surface?: string;
  last_exposed_at?: string;
  last_paywall_at?: string;
  last_echo_started_at?: string;
  last_echo_delivered_at?: string;
  last_rating_at?: string;
  last_offer_prompted_at?: string;
  last_offer_selected_at?: string;
  last_checkout_created_at?: string;
  last_payment_at?: string;
  last_payment_failed_at?: string;
  last_payment_expired_at?: string;
  last_refund_at?: string;
  last_partial_refund_at?: string;
  last_dispute_at?: string;
  last_dispute_won_at?: string;
  conversion_outcome?: MonetizationOutcome;
  journey_column?: MonetizationJourneyColumnKey;
  furthest_stage?: string;
  post_offer_event_count?: number;
  journey_events?: ExperimentJourneyEvent[];
  metrics: ExperimentMetrics;
};

export type MonetizationOutcome =
  | "before_offer"
  | "offer_open"
  | "offer_selected"
  | "checkout_open"
  | "checkout_expired"
  | "payment_failed"
  | "paid"
  | "refunded"
  | "disputed";

export type MonetizationJourneyColumnKey =
  | "before_offer"
  | "offer_open"
  | "checkout"
  | "cart_abandoned"
  | "payment_issue"
  | "paid"
  | "reversed";

export type ExperimentJourneyEvent = {
  event_id: string;
  event_type: string;
  occurred_at?: string;
  workload_id?: string;
  order_id?: string;
  offer_id?: string;
  offer_ids?: string[];
  amount_minor?: number;
  refunded_amount_minor?: number;
  currency?: string;
  echo_credits?: number;
  internal_credit_units?: number;
  rating?: number;
};

export type MonetizationJourneyColumn = {
  key: MonetizationJourneyColumnKey;
  label: string;
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  assignments: ExperimentAssignment[];
};

export type MonetizationJourneyBoard = {
  experiment_id: string;
  variant: string | null;
  assignment_count: number;
  page_size: number;
  columns: MonetizationJourneyColumn[];
  truncated: boolean;
};

export type ExperimentAssignmentsPage = {
  data: ExperimentAssignment[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  truncated: boolean;
};

export async function fetchExperimentSummary(
  experimentId = FIRST_ECHO_PAYGATE_EXPERIMENT_ID,
): Promise<ExperimentSummary> {
  const result = await dashboardRequest<{ data: ExperimentSummary }>(
    `/experiments/${encodeURIComponent(experimentId)}/summary`,
  );
  return result.data;
}

export function fetchExperimentAssignments(
  experimentId = FIRST_ECHO_PAYGATE_EXPERIMENT_ID,
  limit = 100,
): Promise<ExperimentAssignmentsPage> {
  const query = new URLSearchParams({ offset: "0", limit: String(limit) });
  return dashboardRequest<ExperimentAssignmentsPage>(
    `/experiments/${encodeURIComponent(experimentId)}/assignments?${query.toString()}`,
  );
}

export async function fetchMonetizationJourneyBoard({
  experimentId = CONTACT_PAYWALL_EXPERIMENT_ID,
  variant = null,
  column,
  offset = 0,
  limit = 20,
}: {
  experimentId?: string;
  /** Restrict the board to one variant; null keeps every paid variant. */
  variant?: string | null;
  column?: MonetizationJourneyColumnKey;
  offset?: number;
  limit?: number;
} = {}): Promise<MonetizationJourneyBoard> {
  const query = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  if (variant) query.set("variant", variant);
  if (column) query.set("column", column);
  const result = await dashboardRequest<{ data: MonetizationJourneyBoard }>(
    `/experiments/${encodeURIComponent(experimentId)}/board?${query.toString()}`,
  );
  return result.data;
}

export function variantLabel(variant: string): string {
  return variant === "pay_after_first_echo"
    ? "Pay after first Echo"
    : variant === "pay_immediately"
      ? "Pay immediately"
      : variant === "free_control"
        ? "Free control"
        : variant;
}

export function isPaidVariant(variant: string): boolean {
  return variant === "pay_after_first_echo" || variant === "pay_immediately";
}

export function formatRate(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

export function formatRevenue(
  amountMinor: number,
  currency: string | null,
): string {
  if (!currency) return String(amountMinor);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

/** Money that only exists once somebody paid; "—" until then. */
export function formatOptionalRevenue(
  amountMinor: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amountMinor == null) return "—";
  return formatRevenue(amountMinor, currency ?? null);
}
